// 🔑 WHAT IS STILL NEEDED ON THE BORROWER'S STEP — ONE SOURCE FOR THE SHELL AND THE STAFF REVIEW TAB (Jesse, 2026-10-23).
// "A header at the top of the Review tab showing the step the borrower is on, when they reached it, and what's still
// needed on that step, in the borrower's own wording, plus the staff detail for each item … one source, no second
// calculation." The shell's step screens read their lists through these functions (GuidedDocumentsStep: the step-5
// "Still needed" sections and needs; GuidedCreditStep: the step-7 needs), and the header (components/admin/
// GuidedStepHeader) runs remainingOnStep over the same payloads, fetched for the borrower by
// GET /api/admin/borrowers/:id/guided-view. test/guidedStepHeader.test.js holds the two lists equal.
import { buildSectionModel, employmentTypes } from './sectionModel';
import { stepByNumber, TOTAL_STEPS } from './guidedFlowSteps';
import { isOurCheck, RECHECKING_MESSAGE } from './guidedReopenReasons';
import { step7Phase } from './guidedStep6and7';
import { STEP7_LINES, STEP7_INVITE_PENDING } from './guidedCopy';
import { accountCards } from './accountCards';

// The step gate's own refusal lines (backend/utils/guidedStepGate MESSAGES — the parity test holds them equal): what a
// borrower is told when a step is not finished. Used for a step whose screen has no list of its own.
export const STEP_MESSAGES = Object.freeze({
  1: 'Please finish "About you" first.',
  2: 'Please finish "Property and funds" first.',
  3: 'Please review and submit your application first.',
  4: 'Please verify your identity first.',
  5: 'Please finish your documents first.',
  6: 'Please confirm your income and savings first.',
  7: 'Please finish the credit step first.',
  8: 'Your decision is not ready yet.',
});
// GuidedSigning's own line on the approved step-8 screen.
export const SIGN_LINE = 'Read the full agreement, then sign below to accept';

/** A need's wording on the step screens (GuidedDocumentsStep / GuidedCreditStep). */
export const needWording = (n) => (n && (n.description || n.title)) || '';
/** Answered (responded) needs are not owed: the screen says "Answered — we will look at this on the next check." */
export const needOwed = (n) => !!n && String(n.status) !== 'responded';

/**
 * The needs a step screen lists. Step 5 hides the self-employed path-choice need when the Income section's income
 * sources card already asks it (GuidedDocumentsStep, 2026-10-22) — not asked twice.
 */
export function needsOnStep(needs, step, formData = null) {
  return (Array.isArray(needs) ? needs : []).filter((n) => {
    if (!n || Number(n.step) !== Number(step)) return false;
    // 🔑 The current-home conflict is answered on Housing's own card ("Which of these fits?", 2026-10-27), and "Still
    // needed: Housing (your current home’s mortgage)" names it — never a generic upload card too.
    if (Number(step) === 5 && (n.source === 'housing_conflict' || n.category === 'housing_conflict')) return false;
    // 🔑 …and the step-5 reopen for a private lender's tax statement and insurance page: Housing's own two rows ask for them.
    if (Number(step) === 5 && (n.source === 'private_tax_insurance' || n.category === 'private_tax_insurance')) return false;
    // 🔑 …and the day-14 statement option (2026-10-28): the VOM sub-card offers it ("Use payment statements instead").
    if (Number(step) === 5 && (n.source === 'private_vom_statement_option' || n.category === 'private_vom_statement_option')) return false;
    if (Number(step) === 5 && n.source === 'self_employed_path_choice') {
      const t = employmentTypes(formData);
      if (t.includes('self_employed') || t.includes('1099')) return false;
    }
    return true;
  });
}

/** The step-5 section model, built from the GET /step5 payload exactly as GuidedDocumentsStep builds it. */
// 🔑 WHILE THE PAY-STUB ASK IS OWED, THE PAY-STUB ROW IS NOT COMPLETE (np-step5, Hannah's mockup review 2026-11-01): the
// row reads the server's "Needed" (backend utils/step5Asks screenState), Income drops below 100% and the step-5 gate
// names it while the ask is owed (not yet uploaded).
function withIncomeAsk(model, payload) {
  const ask = ((payload && payload.needs) || []).find((n) => n && n.ask && n.ask.kind === 'income_paystub' && ['pending', 'responded'].includes(String(n.status)));
  const owed = !!ask;
  const st = payload && payload.askState;
  // 🔑 WHILE THE NEW STUB IS READ NOTHING CHANGES (Hannah, 2026-11-01 — item 11; this replaces the review's "reading =
  // satisfied"): the row keeps "Needed", the section its %, and Continue waits ("Reading your document…") until the read
  // passes and the ask closes.
  if (!model || !owed || !st || !st.income) return model;
  const sec = (model.sections || []).find((x) => x.id === 'income');
  if (!sec) return model;
  let hit = false;
  sec.items = sec.items.map((i) => (st.income.rows.includes(i.id) ? (hit = true, { ...i, provided: false, askNeeded: st.rowNeeded }) : i));
  if (!hit) return model;
  const required = sec.items.filter((i) => !i.optional);
  sec.providedPct = required.length ? Math.min(99, Math.round((required.filter((i) => i.provided).length / required.length) * 100)) : 99;
  const counted = (model.sections || []).filter((x) => (model.counted || []).includes(x.id));
  if (counted.length) model.providedPct = Math.round(counted.reduce((a, x) => a + x.providedPct, 0) / counted.length);
  sec.provided = false;
  sec.outstanding = required.filter((i) => !i.provided).length;
  model.gate = { ready: false, missing: [...new Set([...(model.gate.missing || []), sec.label])] };
  return model;
}

// 🔑 WHILE AN ACCOUNT'S REQUEST IS OPEN, ASSETS IS NOT COMPLETE (Hannah, 2026-11-01 — the account cards): the account's
// card shows "Needed" (constants/accountCards), Assets drops below 100%, the "Account Statements" line loses its tick and
// "Still needed" names Assets — until the request is answered (an upload or a written explanation).
export function withAccountAsks(model, payload) {
  if (!model || !payload || payload.isCoBorrower) return model;
  const { cards, anyOpen } = accountCards(payload, needsOnStep(payload.needs, 5, payload.formData));
  if (!anyOpen) return model;
  const sec = (model.sections || []).find((x) => x.id === 'assets');
  if (!sec) return model;
  const done = cards.filter((c) => !c.open).length;
  const share = cards.length ? Math.round((done / cards.length) * 100) : 0;
  sec.providedPct = Math.min(99, Number(sec.providedPct) || 0, Math.max(50, share));
  sec.provided = false;
  if (sec.completion) sec.completion = { ...sec.completion, pct: sec.providedPct, complete: false };
  const counted = (model.sections || []).filter((x) => (model.counted || []).includes(x.id));
  if (counted.length) model.providedPct = Math.round(counted.reduce((a, x) => a + x.providedPct, 0) / counted.length);
  model.gate = { ready: false, missing: [...new Set([...((model.gate && model.gate.missing) || []), sec.label])] };
  return model;
}

export function step5ModelOf(payload) {
  if (!payload) return null;
  return withAccountAsks(withIncomeAsk(buildSectionModel({
    formData: payload.formData,
    documents: payload.documents,
    reviewSections: payload.reviewSections,
    assetSources: payload.assetSources,
    selfEmployedPath: payload.selfEmployedPath,
    kycStatus: payload.kycStatus,
    creditPulled: payload.creditPulled,
    ownerMortgage: payload.ownerMortgage,
    rentVerification: payload.rentVerification,
    rentals: payload.rentals,   // rentals and the current home (2026-10-26)
    downPayment: payload.downPayment,
    plaid: payload.plaid,
    giftSale: payload.giftSale,   // each gift's donor request and each home sale's net-sheet request (step-5 fixes, item 3)
    isCoBorrower: payload.isCoBorrower,
    jointAssets: payload.jointAssets || null,   // 🔑 Joint Part 2 B: the file's verified money (null when solo)
    includeIdentity: false,
  }), payload), payload);
}

/** The sections named in step 5's "Still needed: …" line. */
export const step5StillNeeded = (model) => (model && model.gate && !model.gate.ready ? model.gate.missing.map((label) => {
  // 🔑 A section plus ONE level of detail, never two (Jesse, 2026-10-28): "Housing (mortgage history)". Housing is the
  // only section that names its parts; the others are named alone ("Income", "Assets").
  const sec = (model.sections || []).find((x) => x.label === label);
  return sec && sec.id === 'housing' && sec.stillNeeded ? `${label} (${sec.stillNeeded})` : label;
}) : []);

const sectionStaffDetail = (model, label) => {
  const sec = ((model && model.sections) || []).find((s) => s.label === label);
  const open = sec ? sec.items.filter((i) => !i.optional && !i.provided) : [];
  return { kind: 'documents', section: sec ? sec.id : null,
    documents: open.map((i) => ({ label: i.label, docType: i.docType || null })) };
};
const needStaffDetail = (n) => ({ kind: 'need', needId: n.id, category: n.category || null, docType: n.docType || null,
  source: n.source || null, status: n.status || null, staffText: n.description && n.title && n.title !== n.description ? n.title : null });

/**
 * @param {{ state, needs, step5, letters }} v — GET /state, GET /needs' list, GET /step5, the step-7 letters
 * @returns {{ step, label, totalSteps, reachedAt, items: {key,text,staff}[], note: string|null, declined, awaitingSignature }}
 */
// The borrower's own words for a held pull (backend utils/fraudFlagPlacement PULL_HELD_MESSAGE's heading).
export const STEP7_CONFIRMING_NOTE = 'We\u2019re confirming a few details';

export function remainingOnStep(v = {}) {
  const st = v.state || {};
  const step = Number(st.gate && st.gate.currentStep) || Number(st.step) || 1;
  const s = stepByNumber(step);
  const gateStep = ((st.gate && st.gate.steps) || []).find((x) => Number(x.n) === step) || null;
  const code = gateStep && gateStep.code;
  const out = { step, label: s ? s.label : `Step ${step}`, totalSteps: TOTAL_STEPS, reachedAt: st.reachedAt || null,
    items: [], note: null, declined: !!st.declined, awaitingSignature: !!st.awaitingSignature, code: code || null };
  const formData = v.step5 ? v.step5.formData : null;

  if (out.declined) { out.note = 'Declined — the borrower sees the declined screen on step 8.'; return out; }
  if (step === 8) {
    if (out.awaitingSignature) out.items.push({ key: 'sign', text: SIGN_LINE, staff: { kind: 'signature' } });
    else if (!st.completedAt) out.note = STEP_MESSAGES[8];
    return out;
  }
  // 🔑 STEPS 6 AND 7: WHOSE MOVE (Jesse, 2026-10-26 — backend utils/stepBlockers). Staff (the Review tab) get one item per
  // owner with the Module steps that do it; the borrower's own header never gets the staff detail.
  if ((step === 6 || step === 7) && v.stepBlockers && v.stepBlockers.items && v.stepBlockers.items.length) {
    const b = v.stepBlockers;
    if (b.staffLine) {
      const actions = b.items.filter((x) => x.owner === 'staff').flatMap((i) => i.modules || []);
      out.items.push({ key: `step${step}:staff`, text: b.staffLine, staff: { kind: 'blockers', owner: 'staff', actions } });
    }
    if (b.borrowerLine) out.items.push({ key: `step${step}:borrower`, text: b.borrowerLine, staff: { kind: 'blockers', owner: 'borrower', actions: [] } });
    return out;
  }
  if (step === 5) {
    const model = step5ModelOf(v.step5);
    const named = step5StillNeeded(model);
    (model && model.gate && !model.gate.ready ? model.gate.missing : []).forEach((label, i) => {
      out.items.push({ key: `section:${label}`, text: named[i] || label, staff: sectionStaffDetail(model, label) });
    });
  }
  // 🔑 STEP 7 IS A SCREEN PER PHASE: the shell's own step7Phase over the GET /step7 payload decides which, and the
  // header quotes that screen's heading (constants/guidedCopy STEP7_LINES) — an action the borrower owes is an item;
  // a wait (on us, or on the other borrower) is a note.
  let needs = v.needs;
  if (step === 7 && v.step7) {
    const d = v.step7;
    const phase = step7Phase(d, 7);
    out.phase = phase;
    needs = d.needs;
    const others = Array.isArray(d.others) ? d.others : [];
    const owe = (text) => out.items.push({ key: `step7:${phase}`, text, staff: { kind: 'step7', phase } });
    if (phase === 'authorize') owe(STEP7_LINES.authorize);
    else if (phase === 'partner') owe(d.partnerCredit && d.partnerCredit.path === 'reissue' ? STEP7_LINES.reissue : STEP7_LINES.authorize);
    else if (phase === 'joint_intent') owe(STEP7_LINES.jointIntent);
    else if (phase === 'reuse') owe(STEP7_LINES.reuse);
    else if (phase === 'invite_pending') out.note = STEP7_INVITE_PENDING.heading((d.pendingInvite && d.pendingInvite.firstName) || 'your co-borrower');
    else if (phase === 'waiting') {
      out.note = others.length === 1 ? STEP7_LINES.waitingOn(others[0].firstName) : STEP7_LINES.waitingOnMany;
      out.noteDetail = others.map((o) => `${o.firstName}: ${o.declined ? 'declined the credit authorization'
        : o.authorized ? 'authorized' : 'not yet authorized'}`).join('; ');   // their own step is their own column
    } else if (phase === 'blocked') {
      out.note = d.step6 && (d.step6.openDisputes || []).length ? STEP7_LINES.disputeOpen : STEP7_LINES.notConfirmed;
    } else if (phase === 'declined') out.note = STEP7_LINES.declined;
    else if (phase === 'pulling' || phase === 'pulled') out.note = STEP7_LINES.checking;
    else if (phase === 'confirming') out.note = STEP7_CONFIRMING_NOTE;
  }
  if (step === 7 && (!v.step7 || out.phase === 'conditions')) {
    for (const l of (Array.isArray(v.letters) ? v.letters : [])) {
      out.items.push({ key: `letter:${l.id}`, text: l.text, staff: { kind: 'letter', letterId: l.id, ...(l.staff || {}) } });
    }
  }
  if (!(step === 7 && v.step7 && out.phase !== 'conditions')) {
    for (const n of needsOnStep(needs, step, formData).filter(needOwed)) {
      out.items.push({ key: `need:${n.id}`, text: needWording(n), staff: needStaffDetail(n) });
    }
  }
  // Our own re-check owes the borrower nothing — said only when nothing else is owed on the step.
  if (isOurCheck(code) && !out.items.length) out.note = RECHECKING_MESSAGE;
  else if (!out.items.length && !out.note && code) out.items.push({ key: `gate:${code}`, text: STEP_MESSAGES[step], staff: { kind: 'gate', code } });
  return out;
}

// ── 🔑 STAFF WORDING (Jesse, 2026-10-26): the DoorlyDesk Review tab and the staff step header are staff-facing — never
// copies of the borrower's lines ("Please confirm your income and savings first"). Same items (same keys, the shell's
// rules, one source); the words say whose move it is and what is owed.
export const STAFF_STEP_MESSAGES = Object.freeze({
  1: 'Waiting on borrower: finish "About you"',
  2: 'Waiting on borrower: finish "Property and funds"',
  3: 'Waiting on borrower: review, certify and submit the application',
  4: 'Waiting on borrower: verify their identity',
  5: 'Waiting on borrower: finish the step-5 documents',
  6: 'Waiting on borrower: confirm income and assets',
  7: 'Waiting on borrower: finish the credit step',
  8: 'The decision is not in yet',
});
const lc = (t) => { const x = String(t || '').trim(); return x ? x.charAt(0).toLowerCase() + x.slice(1) : x; };
const STAFF_STEP7 = Object.freeze({ authorize: 'Waiting on borrower: authorize the credit check', partner: 'Waiting on borrower: authorize the credit check',
  joint_intent: 'Waiting on borrower: affirm the joint application', reuse: 'Waiting on borrower: continue with the credit report on file' });

function staffText(it, step) {
  const k = it && it.staff ? it.staff.kind : null;
  if (k === 'blockers') return it.text;                                        // already staff-facing (backend utils/stepBlockers)
  if (k === 'need') return `Waiting on borrower: ${lc(it.text).replace(/\.$/, '')}${it.staff.needId ? ` (need #${it.staff.needId})` : ''}`;
  if (k === 'documents') return `Waiting on borrower: ${lc(it.text)} documents`;
  if (k === 'signature') return 'Waiting on borrower: sign the program agreement';
  if (k === 'letter') return `Waiting on borrower: write a letter of explanation — ${lc(it.text).replace(/\.$/, '')}${it.staff.letterId ? ` (letter #${it.staff.letterId})` : ''}`;
  if (k === 'step7') return STAFF_STEP7[it.staff.phase] || STAFF_STEP_MESSAGES[7];
  if (k === 'gate') return STAFF_STEP_MESSAGES[step] || it.text;
  return it.text;
}

function staffNote(r) {
  if (!r.note) return null;
  if (r.declined) return 'Declined — the borrower sees the declined screen on step 8.';
  if (r.step === 8) return STAFF_STEP_MESSAGES[8];
  if (r.step === 7) {
    const ph = r.phase;
    if (ph === 'pulling' || ph === 'pulled') return 'Credit pull running';
    if (ph === 'confirming') return 'Pull held by an open high fraud flag — see Waiting on staff';
    if (ph === 'waiting') return r.note;   // "Waiting on <name>" — already says whose move
    if (ph === 'invite_pending') return 'Waiting on the invited co-borrower to join';
    if (ph === 'declined' || ph === 'other_declined') return 'The credit check was declined';
    if (ph === 'blocked') return 'Step 6 isn\u2019t complete (an open dispute, or the figures not confirmed)';
  }
  if (r.note === RECHECKING_MESSAGE) return 'Our step-5 re-check is running — nothing is owed by the borrower';
  return r.note;
}

/** The shell's remaining items in staff words — for the Review tab and the staff step header. */
export function staffRemainingOnStep(v = {}) {
  const r = remainingOnStep(v);
  return { ...r, items: r.items.map((it) => ({ ...it, borrowerText: it.text, text: staffText(it, r.step) })), note: staffNote(r) };
}

